/**
 * Wire protocol + limits shared by the server and the browser client.
 *
 * This file is plain ESM with no dependencies so that Node can import it
 * directly and Vite can bundle it through the `@shared` alias.
 */

export const PROTOCOL_VERSION = 1;

/** Hard cap on simultaneous drawers in one pad. */
export const MAX_MEMBERS = 5;
/** Extra read-only viewers allowed when the host enables spectators. */
export const MAX_SPECTATORS = 15;

/** Join codes avoid 0/O/1/I/L to stay readable when spoken aloud. */
export const CODE_ALPHABET = 'ABCDEFGHJKMNPQRSTUVWXYZ23456789';
export const CODE_LENGTH = 6;

/** Client -> server messages. */
export const C2S = {
  HELLO: 'hello',
  STROKE_BEGIN: 'stroke:begin',
  STROKE_POINTS: 'stroke:points',
  STROKE_END: 'stroke:end',
  OP: 'op',
  OP_UPDATE: 'op:update',
  OP_DELETE: 'op:delete',
  OP_RESTORE: 'op:restore',
  CLEAR: 'clear',
  CURSOR: 'cursor',
  VIEWPORT: 'viewport',
  LASER: 'laser',
  /**
   * An op being dragged out but not yet committed — the rectangle you are
   * still sizing. Relayed to peers and never written to disk, so everyone
   * watches the shape grow instead of seeing it appear finished.
   */
  DRAFT: 'draft',
  CHAT: 'chat',
  PAD_UPDATE: 'pad:update',
  KICK: 'kick',
  PING: 'ping',
};

/** Server -> client messages. */
export const S2C = {
  WELCOME: 'welcome',
  SNAPSHOT: 'snapshot',
  PEER_JOIN: 'peer:join',
  PEER_LEAVE: 'peer:leave',
  STROKE_BEGIN: 'stroke:begin',
  STROKE_POINTS: 'stroke:points',
  STROKE_END: 'stroke:end',
  OP: 'op',
  OP_UPDATE: 'op:update',
  OP_DELETE: 'op:delete',
  OP_RESTORE: 'op:restore',
  CLEAR: 'clear',
  CURSOR: 'cursor',
  VIEWPORT: 'viewport',
  LASER: 'laser',
  DRAFT: 'draft',
  CHAT: 'chat',
  PAD_UPDATE: 'pad:update',
  KICKED: 'kicked',
  ERROR: 'error',
  PONG: 'pong',
};

/** Op kinds that live in the persistent op log. */
export const OP = {
  STROKE: 'stroke',
  SHAPE: 'shape',
  STAMP: 'stamp',
  TEXT: 'text',
  IMAGE: 'image',
  /** A flood fill, stored as the outline it produced rather than as pixels. */
  FILL: 'fill',
};

/** Drawing tools available in the toolbar. */
export const TOOLS = {
  PEN: 'pen',
  MARKER: 'marker',
  HIGHLIGHTER: 'highlighter',
  NEON: 'neon',
  ERASER: 'eraser',
  OBJECT_ERASER: 'object-eraser',
  /** Pick things up, move them, resize them. */
  SELECT: 'select',
  /** One tool for every shape; which one is a separate selection. */
  SHAPE: 'shape',
  /** Paint-bucket: flood the region under the pointer. */
  FILL: 'fill',
  TEXT: 'text',
  STAMP: 'stamp',
  LASER: 'laser',
  PAN: 'pan',
};

/** Brush sizes, mirroring the six-dot size strip in the UI. */
export const BRUSH_SIZES = [1.5, 3, 6, 12, 24, 44];

/** Default palette. Users can also pick any custom colour. */
export const PALETTE = [
  '#1a1859', '#02a8fe', '#a51bd7', '#f7288e', '#ffffff',
  '#111418', '#6b7280', '#ef4444', '#f97316', '#f59e0b',
  '#22c55e', '#14b8a6', '#3b82f6', '#6366f1', '#92400e',
];

/** Colours assigned to peers for cursors / presence chips. */
export const AVATAR_COLORS = [
  '#02a8fe', '#f7288e', '#a51bd7', '#22c55e', '#f59e0b',
  '#14b8a6', '#ef4444', '#6366f1', '#f97316', '#0ea5e9',
];

/** Camera limits for the (almost) infinite canvas. */
export const ZOOM_MIN = 0.02;
export const ZOOM_MAX = 8;
export const ZOOM_START = 8;
/**
 * How far past the edge of existing artwork a user may pan, expressed in
 * viewport-fulls. Drawing anything new pushes this frontier outwards.
 */
export const PAN_MARGIN_SCREENS = 1.5;

/** Size caps so one user cannot wedge a pad for everyone else. */
export const LIMITS = {
  maxPointsPerStroke: 20000,
  maxPointsPerMessage: 512,
  /** A fill is stored as traced outlines; cap how intricate one may be. */
  maxFillRings: 240,
  maxFillPoints: 12000,
  maxImageBytes: 3 * 1024 * 1024,
  maxTextLength: 512,
  maxChatLength: 500,
  maxOpsPerPad: 200000,
  maxPadsPerUser: 200,
  maxTitleLength: 60,
};

/** Rate limits applied per connection on the server. */
export const RATE = {
  messagesPerSecond: 240,
  burst: 600,
};

/** What a clear button wipes. */
export const CLEAR_SCOPES = ['mine', 'mine-drawing', 'mine-images', 'all'];

export const ERRORS = {
  PAD_FULL: 'pad_full',
  BAD_PASSWORD: 'bad_password',
  NOT_FOUND: 'not_found',
  LOCKED: 'locked',
  UNAUTHORIZED: 'unauthorized',
  RATE_LIMITED: 'rate_limited',
  TOO_LARGE: 'too_large',
};

/** Default per-pad settings the host controls. */
export function defaultPadSettings() {
  return {
    background: 'dots',
    allowSpectators: false,
    locked: false,
    allowGuestClearAll: false,
    showPeerCursors: true,
    showPeerViewports: true,
    chatEnabled: true,
    laserEnabled: true,
  };
}

/** Default per-user preferences, stored on the account and in localStorage. */
export function defaultUserSettings() {
  return {
    theme: 'light',
    background: 'dots',
    showCursors: true,
    showCursorNames: true,
    showPeerViewports: true,
    pressure: true,
    smoothing: true,
    sounds: false,
    haptics: true,
    autosaveThumbnails: true,
    invertScrollZoom: false,
    leftHanded: false,
    showFps: false,
    confirmClear: true,
    followSnapEase: true,
  };
}
